import React from "react";
import styled from "styled-components";
import { Card, Empty, Progress, Skeleton, Space, Tag, Typography } from "antd";

const { Text, Title } = Typography;

const FunnelCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;

  .ant-card-head-title {
    color: #005249;
    font-weight: 700;
  }
`;

const StageList = styled.div`
  display: grid;
  gap: 14px;
`;

const StageRow = styled.div`
  border: 1px solid ${({ $highlight }) => ($highlight ? "#ffccc7" : "#f0f0f0")};
  border-radius: 10px;
  padding: 14px;
  background: ${({ $highlight }) => ($highlight ? "#fff2f0" : "#ffffff")};
`;

const StageHeader = styled(Space)`
  width: 100%;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const StageMeta = styled(Space)`
  margin-top: 8px;
`;

const CountText = styled(Text)`
  color: #005249;
  font-size: 22px;
  font-weight: 700;
`;

const RevenueLeakageFunnel = ({
  funnel,
  loading = false,
  title = "Revenue Leakage Funnel",
}) => {
  if (loading) {
    return (
      <FunnelCard title={title} data-testid="revenue-funnel-loading">
        <Skeleton active paragraph={{ rows: 6 }} />
      </FunnelCard>
    );
  }

  if (!hasFunnelData(funnel)) {
    return (
      <FunnelCard title={title}>
        <Empty
          description="No funnel data for the selected filters"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </FunnelCard>
    );
  }

  return (
    <FunnelCard
      title={title}
      extra={
        funnel.largestLeakage ? (
          <Tag color="red">Largest leak: {funnel.largestLeakage.label}</Tag>
        ) : (
          <Tag color="green">No drop-off detected</Tag>
        )
      }
    >
      <StageList data-testid="revenue-leakage-funnel">
        {funnel.stages.map((stage) => (
          <StageRow
            key={stage.key}
            $highlight={stage.isLargestLeakage}
            data-drilldown-key={stage.drilldownKey}
          >
            <StageHeader align="start">
              <Space direction="vertical" size={0}>
                <Title level={5}>{stage.label}</Title>
                <Text type="secondary">
                  {stage.stageConversionPercent}% from previous stage
                </Text>
              </Space>
              <CountText>{formatCount(stage.count)}</CountText>
            </StageHeader>
            <Progress
              percent={stage.cumulativeConversionPercent}
              showInfo={false}
              strokeColor={stage.isLargestLeakage ? "#ff4d4f" : "#005249"}
              trailColor="#f0f0f0"
            />
            <StageMeta size={[8, 8]} wrap>
              <Tag color={stage.dropOffPercent > 0 ? "orange" : "default"}>
                {stage.dropOffPercent}% drop-off
              </Tag>
              <Text type="secondary">{formatDropOff(stage.dropOffCount)}</Text>
              {stage.value !== null && (
                <Text type="secondary">Value: {formatStageValue(stage)}</Text>
              )}
            </StageMeta>
          </StageRow>
        ))}
      </StageList>
    </FunnelCard>
  );
};

function hasFunnelData(funnel) {
  return (funnel?.stages || []).some((stage) => stage.count > 0);
}

function formatCount(value) {
  return new Intl.NumberFormat("en-ZA").format(Number(value || 0));
}

function formatDropOff(value) {
  const count = Number(value || 0);
  return count === 1 ? "1 record lost" : `${formatCount(count)} records lost`;
}

function formatStageValue(stage) {
  if (stage.valueUnit === "currency_zar") {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
      maximumFractionDigits: 0,
    }).format(Number(stage.value || 0));
  }

  return formatCount(stage.value);
}

export default RevenueLeakageFunnel;
